import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ROLES } from "../constants/roles";
import { useFetchFeatureConfig } from "../hooks/useFetchFeatureConfig";
import { useOrganizationSelf } from "../hooks/useOrganizationSelf";
import { axiosInstance } from "../lib/axios";
import { useUploadFile } from "../upload/hooks/useUploadFile";
import { isCustomerBrandingEnabled } from "../utils/isEnv";

import type {
  CustomerBranding,
  CustomerBrandingLogo,
  CustomerBrandingPayload,
} from "./types";

const BRANDING_ROUTE = "/branding";
const BRANDING_QUERY_KEY = ["customer-brandings"];

export const useCanUseCustomerBranding = () => {
  const brandingEnabled = isCustomerBrandingEnabled();
  const { data: organization } = useOrganizationSelf(brandingEnabled);
  const { data: featureConfig } = useFetchFeatureConfig();
  const brandingProfilesEnabled = featureConfig?.branding_profiles ?? false;
  const role = organization?.role;
  return (
    brandingEnabled &&
    brandingProfilesEnabled &&
    (role === ROLES.SUPERADMIN || role === ROLES.ADMIN)
  );
};

type BrandingsQueryOptions = { staleTime?: number };

export const useCustomerBrandings = (
  enabled = true,
  options: BrandingsQueryOptions = {},
) =>
  useQuery({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: async () => {
      const { data } = await axiosInstance.get<{ results: CustomerBranding[] }>(
        BRANDING_ROUTE,
      );
      return data.results;
    },
    enabled,
    ...options,
  });

export const useCreateCustomerBranding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CustomerBrandingPayload) =>
      axiosInstance.post<{ result: CustomerBranding }>(BRANDING_ROUTE, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
    },
  });
};

export const useUpdateCustomerBranding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: CustomerBrandingPayload & { id: string }) =>
      axiosInstance.put<{ result: CustomerBranding }>(
        `${BRANDING_ROUTE}/${id}`,
        payload,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
    },
  });
};

export const useDeleteCustomerBranding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      axiosInstance.delete<{ message: string }>(`${BRANDING_ROUTE}/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
    },
  });
};

type CustomerBrandingUploadConfigResponse = {
  uploadConfig: {
    uploadLink: string;
    metadataFields?: Record<string, unknown>;
    headers?: Record<string, string>;
  };
  bucket: string;
  key: string;
};

export const useUploadCustomerBrandingLogo = () => {
  const uploadFile = useUploadFile();

  return useMutation({
    mutationFn: async (file: File): Promise<CustomerBrandingLogo> => {
      const { data } =
        await axiosInstance.get<CustomerBrandingUploadConfigResponse>(
          `${BRANDING_ROUTE}/upload-config`,
          { params: { mime_type: file.type, file_name: file.name } },
        );

      await uploadFile.mutateAsync({
        uploadUrl: data.uploadConfig.uploadLink,
        file,
        metadataFields: data.uploadConfig.metadataFields ?? {},
        headers: data.uploadConfig.headers,
      });

      return {
        bucket: data.bucket,
        key: data.key,
        metadata: { name: file.name, mime_type: file.type, size: file.size },
      };
    },
  });
};
